import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  useTheme,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  Grid,
  Button,
  Paper,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoIcon from "@mui/icons-material/Info";
import GavelIcon from "@mui/icons-material/Gavel";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { useTranslations } from "../utils/translation";
import { formatNoticeCalendarDate, isAssignmentEffective } from "../utils/notice-effective-date";
import { getLocalizedNoticeContent } from "../utils/notice-content";
import noticesService, {
  NoticeListItem,
  NoticesSummary,
  NoticeFilters,
  NoticeType,
  SourceType,
} from "../services/notices-service";
import NoticeDetailModal from "../components/NoticeDetailModal";

interface NoticesPageProps {
  clientData?: { subdomain: string; clientName?: string };
}

interface FiltersState {
  noticeType: NoticeType | "CONSENT_NOTICE" | "ALL";
  showAcknowledged: boolean;
}

const defaultFilters: FiltersState = {
  noticeType: "ALL",
  showAcknowledged: false,
};

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getNoticeTypeLabel = (notice: NoticeListItem): string => {
  if (notice.sourceType === "CONSENT_NOTICE") {
    return "Consent Notice";
  }
  switch (notice.NoticeType) {
    case "BREACH_NOTICE":
      return "Breach Notice";
    case "ERASURE_NOTICE":
      return "Erasure Notice";
    case "INFORMATIONAL_NOTICE":
      return "Informational Notice";
    default:
      return "Notice";
  }
};

const getNoticeTypeColor = (
  notice: NoticeListItem
): "default" | "primary" | "success" | "warning" | "error" | "info" => {
  if (notice.sourceType === "CONSENT_NOTICE") {
    return "primary";
  }
  switch (notice.NoticeType) {
    case "BREACH_NOTICE":
      return "error";
    case "ERASURE_NOTICE":
      return "warning";
    case "INFORMATIONAL_NOTICE":
      return "info";
    default:
      return "default";
  }
};

const getNoticeTypeIcon = (notice: NoticeListItem) => {
  if (notice.sourceType === "CONSENT_NOTICE") {
    return <GavelIcon fontSize="small" />;
  }
  switch (notice.NoticeType) {
    case "BREACH_NOTICE":
      return <WarningAmberIcon fontSize="small" />;
    case "ERASURE_NOTICE":
      return <DeleteForeverIcon fontSize="small" />;
    case "INFORMATIONAL_NOTICE":
      return <InfoIcon fontSize="small" />;
    default:
      return <InfoIcon fontSize="small" />;
  }
};

const NoticesPage: React.FC<NoticesPageProps> = ({ clientData }) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  // Use English for UI translations (UI is always in English)
  const t = useTranslations("en");
  // Use English for list view content (full content with language selection is in detail modal)
  const listViewLanguage = "en";

  const [loading, setLoading] = useState<boolean>(true);
  const [summaryLoading, setSummaryLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<NoticesSummary>({
    total: 0,
    unacknowledged: 0,
    acknowledged: 0,
    byType: {
      consent: 0,
      breach: 0,
      erasure: 0,
      informational: 0,
    },
  });
  const [notices, setNotices] = useState<NoticeListItem[]>([]);
  const [filters, setFilters] = useState<FiltersState>(defaultFilters);
  const [selectedNotice, setSelectedNotice] = useState<NoticeListItem | null>(null);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  // Track if we've already processed assignmentId from location state to prevent reopening
  const hasProcessedStateAssignmentId = useRef<boolean>(false);

  const loadSummary = useCallback(async () => {
    try {
      setSummaryLoading(true);
      const data = await noticesService.getNoticesSummary();
      setSummary(data);
    } catch (err: any) {
      console.error("Error loading summary:", err);
      setSummary({
        total: 0,
        unacknowledged: 0,
        acknowledged: 0,
        byType: { consent: 0, breach: 0, erasure: 0, informational: 0 },
      });
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const loadNotices = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const apiFilters: NoticeFilters = {
        noticeType: filters.noticeType === "ALL" ? undefined : filters.noticeType,
        showAcknowledged: filters.showAcknowledged,
      };

      const data = await noticesService.getNotices(apiFilters);
      setNotices(data);
      
      // Check if assignmentId is provided in location state (from email link)
      // Only process it once to prevent reopening when modal is closed
      const assignmentIdFromState = (location.state as any)?.assignmentId;
      if (assignmentIdFromState && !hasProcessedStateAssignmentId.current && data.length > 0) {
        const notice = data.find((n) => n.AssignmentId === assignmentIdFromState);
        if (notice) {
          setSelectedNotice(notice);
          setModalOpen(true);
          hasProcessedStateAssignmentId.current = true;
          // Clear the location state using navigate to prevent reopening
          navigate(location.pathname, { replace: true, state: {} });
        }
      }
    } catch (err: any) {
      console.error("Error loading notices:", err);
      setError(err?.message || "Failed to load notices");
      setNotices([]);
    } finally {
      setLoading(false);
    }
  }, [filters, location.state]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadNotices();
  }, [loadNotices]);

  const handleCardClick = (notice: NoticeListItem) => {
    setSelectedNotice(notice);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedNotice(null);
    // Refresh notices after modal closes (in case acknowledgment happened)
    loadNotices();
    loadSummary();
  };

  const handleNoticeTypeChange = (e: SelectChangeEvent<string>) => {
    const value = e.target.value;
    setFilters((prev) => ({
      ...prev,
      noticeType: value as typeof filters.noticeType,
    }));
  };

  const handleShowAcknowledgedToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters((prev) => ({
      ...prev,
      showAcknowledged: e.target.checked,
    }));
  };

  const handleRefresh = () => {
    loadSummary();
    loadNotices();
  };

  const renderSummaryCards = () => (
    <Grid container spacing={1.5} sx={{ mb: 2 }}>
      <Grid item xs={6} sm={3}>
        <Card elevation={1}>
          <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <InfoIcon fontSize="small" color="primary" />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                  {summaryLoading ? <CircularProgress size={20} /> : summary.total}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                  {t("totalNotices")}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={6} sm={3}>
        <Card
          elevation={1}
          sx={{
            borderLeft: summary.unacknowledged > 0 ? `3px solid ${theme.palette.error.main}` : undefined,
          }}
        >
          <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <WarningAmberIcon fontSize="small" color={summary.unacknowledged > 0 ? "error" : "action"} />
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    lineHeight: 1.2,
                    color: summary.unacknowledged > 0 ? theme.palette.error.main : undefined,
                  }}
                >
                  {summaryLoading ? <CircularProgress size={20} /> : summary.unacknowledged}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                  {t("unacknowledged")}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={6} sm={3}>
        <Card elevation={1}>
          <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CheckCircleIcon fontSize="small" color="success" />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                  {summaryLoading ? <CircularProgress size={20} /> : summary.acknowledged}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                  {t("acknowledged")}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={6} sm={3}>
        <Card elevation={1}>
          <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                {t("noticeType")}
              </Typography>
              <Stack spacing={0.25}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
                    {t("consentNotices")}:
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, fontSize: "0.7rem" }}>
                    {summary.byType.consent}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
                    {t("breachNotices")}:
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, fontSize: "0.7rem" }}>
                    {summary.byType.breach}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
                    {t("erasureNotices")}:
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, fontSize: "0.7rem" }}>
                    {summary.byType.erasure}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
                    {t("informationalNotices")}:
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, fontSize: "0.7rem" }}>
                    {summary.byType.informational}
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderFilters = () => (
    <Paper elevation={1} sx={{ p: 1.5, mb: 2 }}>
      <Stack spacing={1}>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          <Select
            value={filters.noticeType}
            onChange={handleNoticeTypeChange}
            size="small"
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="ALL">{t("allNotices")}</MenuItem>
            <MenuItem value="CONSENT_NOTICE">{t("consentNotices")}</MenuItem>
            <MenuItem value="BREACH_NOTICE">{t("breachNotices")}</MenuItem>
            <MenuItem value="ERASURE_NOTICE">{t("erasureNotices")}</MenuItem>
            <MenuItem value="INFORMATIONAL_NOTICE">{t("informationalNotices")}</MenuItem>
          </Select>

          <FormControlLabel
            control={
              <Switch
                checked={filters.showAcknowledged}
                onChange={handleShowAcknowledgedToggle}
                size="small"
              />
            }
            label={filters.showAcknowledged ? t("showAcknowledged") : t("hideAcknowledged")}
          />

          <Box sx={{ flexGrow: 1 }} />

          <IconButton onClick={handleRefresh} size="small" title="Refresh">
            <RefreshIcon />
          </IconButton>
        </Stack>
      </Stack>
    </Paper>
  );

  const getNoticeTitle = (notice: NoticeListItem): string => {
    if (notice.sourceType === "CONSENT_NOTICE") {
      return notice.Name || "Consent Notice";
    }
    // For Other Notices, try to get localized title from ContentJSONSnapshot (default to English for list view)
    if (notice.ContentJSONSnapshot) {
      try {
        const localizedContent = getLocalizedNoticeContent(
          notice.ContentJSONSnapshot,
          listViewLanguage
        );
        if (localizedContent?.title) {
          return localizedContent.title;
        }
      } catch (e) {
        // Fallback to Title field
      }
    }
    return notice.Title || "Notice";
  };

  const getNoticeDescription = (notice: NoticeListItem): string => {
    if (notice.sourceType === "CONSENT_NOTICE") {
      return notice.Description || "";
    }
    // For Other Notices, try to get localized description (default to English for list view)
    if (notice.ContentJSONSnapshot) {
      try {
        const localizedContent = getLocalizedNoticeContent(
          notice.ContentJSONSnapshot,
          listViewLanguage
        );
        if (localizedContent?.information) {
          return localizedContent.information;
        }
      } catch (e) {
        // Fallback to Description field
      }
    }
    return notice.Description || "";
  };

  const renderNoticeCard = (notice: NoticeListItem) => {
    const title = getNoticeTitle(notice);
    const description = getNoticeDescription(notice);
    const isAcknowledged = notice.IsAcknowledged || false;

    return (
      <Grid item key={notice.AssignmentId} xs={12} sm={6} lg={4}>
        <Card
          onClick={() => handleCardClick(notice)}
          sx={{
            height: "100%",
            cursor: "pointer",
            transition: "all 0.2s ease-in-out",
            // Only show red border for unacknowledged non-consent notices
            borderLeft: !isAcknowledged && notice.sourceType !== "CONSENT_NOTICE" ? `4px solid ${theme.palette.error.main}` : undefined,
            "&:hover": {
              transform: "translateY(-4px)",
              boxShadow: theme.shadows[8],
            },
          }}
        >
          <CardContent>
            <Stack spacing={2}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
                  {getNoticeTypeIcon(notice)}
                  <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                    {title}
                  </Typography>
                </Box>
                {/* Acknowledgment badge: for consent notices show "Consented", for others show acknowledged/unacknowledged */}
                {notice.sourceType === "CONSENT_NOTICE" ? (
                  isAcknowledged && (
                    <Chip
                      label={t("consented") || "Consented"}
                      color="success"
                      size="small"
                      icon={<CheckCircleIcon />}
                    />
                  )
                ) : (
                  <Chip
                    label={isAcknowledged ? t("acknowledged") : t("unacknowledged")}
                    color={isAcknowledged ? "success" : "error"}
                    size="small"
                    icon={isAcknowledged ? <CheckCircleIcon /> : <WarningAmberIcon />}
                  />
                )}
              </Box>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                <Chip
                  label={getNoticeTypeLabel(notice)}
                  color={getNoticeTypeColor(notice)}
                  size="small"
                  variant="outlined"
                />
                {notice.EffectiveDate && !isAssignmentEffective(notice.EffectiveDate) && (
                  <Chip label={t("appliesFrom")} size="small" color="info" variant="outlined" />
                )}
              </Box>

              {description && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    minHeight: 40,
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {description}
                </Typography>
              )}

              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t("assignedDate")}:
                </Typography>
                <Typography variant="body2">{formatNoticeCalendarDate(notice.AssignedDate)}</Typography>
              </Box>

              {isAcknowledged && notice.AcknowledgedDate && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t("acknowledgedDate")}:
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.success.main }}>
                    {formatDateTime(notice.AcknowledgedDate)}
                  </Typography>
                </Box>
              )}

              {!isAcknowledged && notice.sourceType !== "CONSENT_NOTICE" && (
                <Chip
                  label={t("actionRequired")}
                  color="error"
                  size="small"
                  sx={{ alignSelf: "flex-start" }}
                />
              )}
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
        {t("notices")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {renderSummaryCards()}
      {renderFilters()}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : notices.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t("noNotices")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("noNoticesMessage")}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {notices.map(renderNoticeCard)}
        </Grid>
      )}

      {selectedNotice && (
        <NoticeDetailModal
          open={modalOpen}
          onClose={handleModalClose}
          assignmentId={selectedNotice.AssignmentId!}
          clientData={clientData}
        />
      )}
    </Box>
  );
};

export default NoticesPage;

